import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Box,
  Typography,
  Chip,
  TextField,
  InputAdornment,
  ListSubheader,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';
import { AccountType } from '../../types/auth';
import { companyApi } from '../../services/api/companyApi';
import { useAuthStore } from '../../store/authStore';
import { Company } from '../../types/company';

interface CompanySelectorProps {
  selectedCompanyId: number | null;
  onCompanySelect: (companyId: number | null) => void;
  disabled?: boolean;
  companies?: Company[];
  showHeader?: boolean;
}

const CompanySelector: React.FC<CompanySelectorProps> = ({
  selectedCompanyId,
  onCompanySelect,
  disabled = false,
  companies: propCompanies,
  showHeader = true,
}) => {
  const { user } = useAuthStore();
  const [companies, setCompanies] = useState<{ id: number; name: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  // keeps the chosen company renderable when a search has narrowed the list
  const selectedRef = useRef<{ id: number; name: string } | null>(null);
  const requestSeq = useRef(0);

  const isMasterAdmin = user?.accountType === AccountType.MasterAdmin || user?.accountType === AccountType.CarbonCareAdmin;
  const isLicenseeAdmin = user?.accountType === AccountType.LicenseeAdmin;
  const shouldShowSelector = isMasterAdmin;

  useEffect(() => {
    if (propCompanies) {
      setCompanies(propCompanies.map(c => ({ id: c.id, name: c.name })));
      return;
    }

    if (shouldShowSelector) {
      loadCompanies();
    } else if (isLicenseeAdmin && user?.companyId) {
      onCompanySelect(user.companyId);
    }
  }, [user, shouldShowSelector, isLicenseeAdmin, propCompanies]);

  const loadCompanies = async (query?: string) => {
    const seq = ++requestSeq.current;
    try {
      query ? setSearching(true) : setLoading(true);
      setError(null);
      const result = await companyApi.getAll({ limit: 500, fields: 'id,name', query: query || undefined });
      if (seq !== requestSeq.current) return; // stale response
      setCompanies(result.data.map((c: any) => ({ id: c.id, name: c.name })));
    } catch (error: any) {
      if (seq !== requestSeq.current) return;
      console.error('Failed to load companies:', error);
      setError('Failed to load companies. Please try again.');
    } finally {
      if (seq === requestSeq.current) { setLoading(false); setSearching(false); }
    }
  };

  // server-side search (debounced) when this component owns the data;
  // prop-supplied lists keep the original client-side filter
  useEffect(() => {
    if (propCompanies || !shouldShowSelector) return;
    const t = setTimeout(() => loadCompanies(search.trim() || undefined), 300);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    if (!propCompanies) return companies; // already server-filtered
    if (!search) return companies;
    const lower = search.toLowerCase();
    return companies.filter(c =>
      c.name.toLowerCase().includes(lower) || c.id.toString().includes(lower)
    );
  }, [companies, search, propCompanies]);

  if (!shouldShowSelector && isLicenseeAdmin && user?.companyId) {
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Uploading for Company:
        </Typography>
        <Chip
          label={user.companyId ? `Company ID: ${user.companyId}` : 'Your Company'}
          color="primary"
          variant="outlined"
        />
      </Box>
    );
  }

  if (!shouldShowSelector) {
    if (user?.companyId && !selectedCompanyId) {
      onCompanySelect(user.companyId);
    }
    return null;
  }

  return (
    <Box sx={{ mb: 0 }}>
      {showHeader && (
        <>
          <Typography variant="h6" gutterBottom>
            Select Company
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Choose the company for which you want to upload and calculate emissions data.
          </Typography>
        </>
      )}

      <FormControl fullWidth sx={{ mt: showHeader ? 2 : 0 }}>
        <InputLabel>{loading ? 'Loading licensees...' : 'Licensee'}</InputLabel>
        <Select
          sx={{
            '& .MuiSelect-icon': {
              backgroundColor: 'background.paper',
              paddingLeft: '4px',
            },
          }}
          value={selectedCompanyId || ''}
          label="Licensee"
          onChange={(e) => {
            const id = e.target.value as number || null;
            selectedRef.current = id ? companies.find(co => co.id === id) || selectedRef.current : null;
            onCompanySelect(id);
          }}
          disabled={disabled || loading}
          onClose={() => setSearch('')}
          renderValue={(selected) => {
            if (!selected) return <em>Select a company</em>;
            const c = companies.find(co => co.id === selected) ||
              (selectedRef.current?.id === selected ? selectedRef.current : null);
            return c ? `${c.name} (ID: ${c.id})` : `ID: ${selected}`;
          }}
          MenuProps={{ autoFocus: false, PaperProps: { sx: { maxHeight: 300 } } }}
        >
          <ListSubheader sx={{ p: 0, lineHeight: 'normal' }}>
            <Box sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Search by name or ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </ListSubheader>
          <MenuItem value="">
            <em>Select a company</em>
          </MenuItem>
          {filtered.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                <span>{c.name}</span>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  ID: {c.id}
                </Typography>
              </Box>
            </MenuItem>
          ))}
          {searching && (
            <MenuItem disabled>
              <CircularProgress size={16} sx={{ mr: 1 }} /> Searching…
            </MenuItem>
          )}
          {!searching && filtered.length === 0 && search && (
            <MenuItem disabled>No companies found</MenuItem>
          )}
        </Select>
      </FormControl>

      {error && (
        <Alert severity="error" sx={{ mt: 2, py: 0.5 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default CompanySelector;
